import React, { useState, useEffect } from 'react';
import { Clock, Check, X } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

interface OrderTicketProps {
  order: {
    id: string;
    items: Array<{ name: string; quantity: number }>;
    total: number;
    createdAt: Date;
    expiresAt: Date;
  };
  onAccept: (id: string) => void;
  onDecline: (id: string) => void;
}

export function OrderTicketCard({ order, onAccept, onDecline }: OrderTicketProps) {
  const [timeLeft, setTimeLeft] = useState<number>(0);

  // Timer logic
  useEffect(() => {
    const updateTimer = () => {
      const now = new Date().getTime();
      const end = new Date(order.expiresAt).getTime();
      const distance = end - now;
      setTimeLeft(Math.max(0, distance));
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [order.expiresAt]);

  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
  
  const isUrgent = timeLeft < 5 * 60 * 1000; // Less than 5 mins

  return (
    <Card className={cn(
      "relative overflow-hidden border-l-4 transition-all duration-300",
      isUrgent ? "border-l-warning shadow-md animate-pulse" : "border-l-primary"
    )}>
      {/* Timer Bar */}
      <div className="absolute top-0 inset-x-0 h-1 bg-gray-100">
        <div 
          className={cn("h-full transition-all duration-1000", isUrgent ? "bg-warning" : "bg-primary")}
          style={{ width: `${(timeLeft / (30 * 60 * 1000)) * 100}%` }} // Assuming 30 min window
        />
      </div>

      <CardHeader className="pb-2 pt-4">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-xs text-body font-medium uppercase tracking-wider">Order #{order.id.slice(-6)}</p>
            <div className={cn("flex items-center space-x-1 mt-1 text-sm font-bold", isUrgent ? "text-warning" : "text-primary")}>
              <Clock className="h-4 w-4" />
              <span>{minutes}:{seconds.toString().padStart(2, '0')}</span>
            </div>
          </div>
          <div className="text-right">
            <p className="font-serif font-bold text-heading text-lg">${order.total.toFixed(2)}</p>
            <p className="text-xs text-body">{order.items.length} items</p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="py-2">
        <div className="space-y-1">
          {order.items.map((item, idx) => (
            <div key={idx} className="flex justify-between text-sm">
              <span className="text-heading font-medium">{item.quantity}x {item.name}</span>
            </div>
          ))}
        </div>
      </CardContent>

      <CardFooter className="pt-2 gap-2">
        <Button 
          variant="outline" 
          onClick={() => onDecline(order.id)}
          className="flex-1 hover:bg-error/10 hover:text-error hover:border-error"
        >
          Decline
        </Button>
        <Button 
          onClick={() => onAccept(order.id)}
          className="flex-1 bg-primary hover:bg-primary-hover text-white"
        >
          Confirm Availability
        </Button>
      </CardFooter>
    </Card>
  );
}

